import React, { useState } from 'react';
import { X, Plus, Trash2, Upload } from 'lucide-react';
import { genericInvoiceAPI, itemsAPI } from "../../services/api";
import { toast } from "react-toastify";

const CreateGenericInvoiceModal = ({ onClose, onCreate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('invoice');
  const [availableItems, setAvailableItems] = useState([]);

  React.useEffect(() => {
    setIsOpen(true);
    fetchAvailableItems();
  }, []);

  const fetchAvailableItems = async () => {
    try {
      const response = await itemsAPI.getAllItems();
      setAvailableItems(response.data.items || []);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  const [formData, setFormData] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    customerPO: '',
    legalBusinessName: '',
    gstNumber: '',
    supplierGstStatus: 'Registered',
    defaultGstRate: 18,
    streetLine1: '',
    streetLine2: '',
    city: '',
    state: '',
    country: 'India',
    zipcode: '',
    accountNo: '',
    ifscCode: '',
    accountName: '',
    bankName: '',
    customerName: '',
    customerGSTIN: '',
    sendToEmail: '',
    paymentTerms: 'Net 30',
    isTdsApplicable: false,
    advanceReceived: 0,
    preferredPaymentMethod: 'Bank Transfer',
    paymentLink: '',
    upiId: '',
    upiNote: '',
    additionalNotes: '',
    authorisedSignatoryName: '',
    designation: '',
    ewayBillNumber: '',
    arnNumber: '',
    fssaiNumber: '',
    packingSlipNumber: '',
    recordPaymentDetails: '',
    items: []
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, {
        itemName: '',
        itemDescription: '',
        hsnOrSac: '',
        itemImageUrl: '',
        quantity: 1,
        rate: 0,
        gstPercent: formData.defaultGstRate || 18
        // amount is calculated locally for display but NOT sent to backend
      }]
    }));
  };

  const removeItem = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const handleItemChange = (index, field, value) => {
    setFormData(prev => {
      const updatedItems = [...prev.items];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value
      };
      
      // Calculate amount for display purposes only (not sent to backend)
      if (field === 'quantity' || field === 'rate') {
        const quantity = field === 'quantity' ? parseFloat(value) || 0 : updatedItems[index].quantity;
        const rate = field === 'rate' ? parseFloat(value) || 0 : updatedItems[index].rate;
        // Store amount separately, not in the item object
        updatedItems[index]._displayAmount = quantity * rate;
      }
      
      return { ...prev, items: updatedItems };
    });
  };

  const selectItemFromMaster = (index, itemId) => {
    if (itemId === 'other') {
      setFormData(prev => {
        const updatedItems = [...prev.items];
        updatedItems[index] = {
          ...updatedItems[index],
          itemName: '',
          itemDescription: '',
          hsnOrSac: '',  // FIXED: Changed from hsnSac to hsnOrSac
          itemImageUrl: ''
        };
        return { ...prev, items: updatedItems };
      });
      return;
    }

    const selectedItem = availableItems.find(item => item.id === itemId);
    if (selectedItem) {
      setFormData(prev => {
        const updatedItems = [...prev.items];
        updatedItems[index] = {
          ...updatedItems[index],
          itemName: selectedItem.itemName,
          itemDescription: selectedItem.itemDescription || '',
          hsnOrSac: selectedItem.hsnSac,  // Note: ItemMaster uses hsnSac, but we store as hsnOrSac
          itemImageUrl: selectedItem.itemImageUrl || ''
        };
        return { ...prev, items: updatedItems };
      });
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.invoiceDate) newErrors.invoiceDate = 'Invoice date is required';
    if (!formData.customerName) newErrors.customerName = 'Customer name is required';
    if (!formData.legalBusinessName) newErrors.legalBusinessName = 'Business name is required';
    if (formData.items.length === 0) newErrors.items = 'At least one item is required';
    
    formData.items.forEach((item, index) => {
      if (!item.itemName) newErrors[`item_${index}_name`] = 'Item name is required';
      if (!item.hsnOrSac) newErrors[`item_${index}_hsn`] = 'HSN/SAC is required';  // FIXED
      if (item.quantity <= 0) newErrors[`item_${index}_qty`] = 'Quantity must be > 0';
      if (item.rate <= 0) newErrors[`item_${index}_rate`] = 'Rate must be > 0';
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotals = () => {
    const subTotal = formData.items.reduce((sum, item) => {
      const amount = item._displayAmount || (item.quantity * item.rate) || 0;
      return sum + amount;
    }, 0);
    const gstAmount = formData.items.reduce((sum, item) => {
      const amount = item._displayAmount || (item.quantity * item.rate) || 0;
      const itemGst = amount * ((item.gstPercent || 0) / 100);
      return sum + itemGst;
    }, 0);
    const tdsAmount = formData.isTdsApplicable ? subTotal * 0.01 : 0;
    const netPayable = subTotal + gstAmount - tdsAmount - (formData.advanceReceived || 0);
    
    return { subTotal, gstAmount, tdsAmount, netPayable };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error('Please fill all required fields');
      setTimeout(() => {
        const el = document.querySelector('.border-red-500');
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus?.(); }
      }, 50);
      return;
    }

    setLoading(true);
    try {
      // Clean items: remove _displayAmount before sending to backend
      const cleanedItems = formData.items.map(item => {
        const { _displayAmount, ...cleanItem } = item;
        return cleanItem;
      });
      
      // Don't send calculated totals - backend will calculate them
      const invoiceData = {
        ...formData,
        items: cleanedItems
        // Remove: subTotal, gstAmount, tdsAmount, netPayable (backend calculates these)
      };

      const formDataToSend = new FormData();
      formDataToSend.append('invoice', JSON.stringify(invoiceData));

      await genericInvoiceAPI.createInvoice(formDataToSend);
      toast.success('Invoice created successfully');
      onCreate();
    } catch (error) {
      console.error('Error creating invoice:', error);
      const errorMessage = error.response?.data?.error || 'Failed to create invoice';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(onClose, 300);
  };

  const tabs = [
    { id: 'invoice', label: 'Invoice Details' },
    { id: 'supplier', label: 'Supplier Info' },
    { id: 'customer', label: 'Customer Info' },
    { id: 'items', label: 'Items' },
    { id: 'payment', label: 'Payment & Notes' }
  ];

  const totals = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
      <div 
        className={`bg-white h-full w-full max-w-4xl shadow-xl flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Create Invoice</h2>
            <p className="text-sm text-gray-500 mt-1">Fill in the details to create a new invoice</p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        <div className="border-b bg-gray-50 px-6">
          <div className="flex gap-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'text-blue-600 border-b-2 border-blue-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-6">
            {activeTab === 'invoice' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Invoice Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Invoice Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="invoiceDate"
                      value={formData.invoiceDate}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.invoiceDate ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Customer PO Number</label>
                    <input
                      type="text"
                      name="customerPO"
                      value={formData.customerPO}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'supplier' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Supplier Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Legal Business Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="legalBusinessName"
                      value={formData.legalBusinessName}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.legalBusinessName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">GST Number</label>
                    <input
                      type="text"
                      name="gstNumber"
                      value={formData.gstNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Default GST Rate (%)</label>
                    <input
                      type="number"
                      name="defaultGstRate"
                      value={formData.defaultGstRate}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Street Line 1</label>
                    <input
                      type="text"
                      name="streetLine1"
                      value={formData.streetLine1}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Street Line 2</label>
                    <input
                      type="text"
                      name="streetLine2"
                      value={formData.streetLine2}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">State</label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Zipcode</label>
                    <input
                      type="text"
                      name="zipcode"
                      value={formData.zipcode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                
                <h4 className="text-md font-medium mt-6">Bank Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Number</label>
                    <input
                      type="text"
                      name="accountNo"
                      value={formData.accountNo}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">IFSC Code</label>
                    <input
                      type="text"
                      name="ifscCode"
                      value={formData.ifscCode}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Account Name</label>
                    <input
                      type="text"
                      name="accountName"
                      value={formData.accountName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Bank Name</label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'customer' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Customer Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">
                      Customer Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-md ${
                        errors.customerName ? 'border-red-500' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Customer GSTIN</label>
                    <input
                      type="text"
                      name="customerGSTIN"
                      value={formData.customerGSTIN}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Send To Email</label>
                    <input
                      type="email"
                      name="sendToEmail"
                      value={formData.sendToEmail}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'items' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium">Items</h3>
                  <button
                    type="button"
                    onClick={addItem}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Add Item
                  </button>
                </div>
                
                {errors.items && (
                  <p className="text-sm text-red-600">{errors.items}</p>
                )}

                {formData.items.map((item, index) => (
                  <div key={index} className="border border-gray-300 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="font-medium">Item #{index + 1}</h4>
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="text-red-600 hover:bg-red-50 p-1 rounded"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">
                          Select Item <span className="text-red-500">*</span>
                        </label>
                        <select
                          onChange={(e) => selectItemFromMaster(index, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">-- Select from Item Master --</option>
                          {availableItems.map(masterItem => (
                            <option key={masterItem.id} value={masterItem.id}>
                              {masterItem.itemName} ({masterItem.hsnSac})
                            </option>
                          ))}
                          <option value="other">-- Add New Item --</option>
                        </select>
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">
                          Item Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={item.itemName}
                          onChange={(e) => handleItemChange(index, 'itemName', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md ${
                            errors[`item_${index}_name`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Description</label>
                        <textarea
                          value={item.itemDescription}
                          onChange={(e) => handleItemChange(index, 'itemDescription', e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          HSN/SAC <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={item.hsnOrSac}
                          onChange={(e) => handleItemChange(index, 'hsnOrSac', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md ${
                            errors[`item_${index}_hsn`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">GST %</label>
                        <input
                          type="number"
                          value={item.gstPercent}
                          onChange={(e) => handleItemChange(index, 'gstPercent', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Quantity <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md ${
                            errors[`item_${index}_qty`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1">
                          Rate <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          value={item.rate}
                          onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-md ${
                            errors[`item_${index}_rate`] ? 'border-red-500' : 'border-gray-300'
                          }`}
                        />
                      </div>

                      <div className="col-span-2">
                        <label className="block text-sm font-medium mb-1">Amount</label>
                        <input
                          type="number"
                          value={item._displayAmount || (item.quantity * item.rate) || 0}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                {formData.items.length > 0 && (
                  <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Subtotal:</span>
                      <span className="font-medium">₹{totals.subTotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>GST Amount:</span>
                      <span className="font-medium">₹{totals.gstAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>TDS Amount:</span>
                      <span className="font-medium">₹{totals.tdsAmount.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Net Payable:</span>
                      <span>₹{totals.netPayable.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'payment' && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Payment Terms</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Payment Terms</label>
                    <select
                      name="paymentTerms"
                      value={formData.paymentTerms}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="Net 15">Net 15</option>
                      <option value="Net 30">Net 30</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                      <option value="Due on Receipt">Due on Receipt</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Payment Method</label>
                    <select
                      name="preferredPaymentMethod"
                      value={formData.preferredPaymentMethod}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="UPI">UPI</option>
                      <option value="Cheque">Cheque</option>
                      <option value="Cash">Cash</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Advance Received (₹)</label>
                    <input
                      type="number"
                      name="advanceReceived"
                      value={formData.advanceReceived}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="isTdsApplicable"
                        checked={formData.isTdsApplicable}
                        onChange={handleChange}
                        className="mr-2"
                      />
                      <span className="text-sm">TDS Applicable (1%)</span>
                    </label>
                  </div>
                </div>

                <h4 className="text-md font-medium mt-6">Notes & Signatures</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Additional Notes</label>
                    <textarea
                      name="additionalNotes"
                      value={formData.additionalNotes}
                      onChange={handleChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Authorised Signatory</label>
                    <input
                      type="text"
                      name="authorisedSignatoryName"
                      value={formData.authorisedSignatoryName}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Designation</label>
                    <input
                      type="text"
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>

                <h4 className="text-md font-medium mt-6">Additional Fields</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">E-Way Bill Number</label>
                    <input
                      type="text"
                      name="ewayBillNumber"
                      value={formData.ewayBillNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">ARN Number</label>
                    <input
                      type="text"
                      name="arnNumber"
                      value={formData.arnNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">FSSAI Number</label>
                    <input
                      type="text"
                      name="fssaiNumber"
                      value={formData.fssaiNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Packing Slip Number</label>
                    <input
                      type="text"
                      name="packingSlipNumber"
                      value={formData.packingSlipNumber}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </form>

        <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0 bg-white">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center gap-2"
            disabled={loading}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              <>
                <Upload size={16} />
                Create Invoice
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGenericInvoiceModal;